import React, { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Search, Eye, ExternalLink, Loader2, ShieldCheck, ShieldAlert, FileSearch,
  CalendarDays, AlertCircle, AlertTriangle, CheckCircle2, Building2, Users,
  Inbox, CheckSquare, MessageSquare, FileText, FileBarChart2, Package, Globe,
  Target, SlidersHorizontal, UserCog, Database, TrendingUp, LogIn, KeyRound, Lock, Link2,
} from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/common";
import { deDT, downloadCsv, ExportBtn, Sel, Pagination, Drawer } from "./shared";
import { A, eventIcon, DeviceCell, DrawerSection, KV, countryFlag } from "./ui";

const RANGE_MS = { "24h": 864e5, "7d": 6048e5, "30d": 2592e6 };
const nf = (n) => (n ?? 0).toLocaleString("de-CH");

const KPI_CONFIG = [
  { key: "total", icon: CalendarDays, accent: "lime" },
  { key: "critical", icon: AlertCircle, accent: "red" },
  { key: "warnings", icon: AlertTriangle, accent: "amber" },
  { key: "successful", icon: CheckCircle2, accent: "emerald" },
  { key: "clients", icon: Building2, accent: "sky" },
  { key: "users", icon: Users, accent: "violet" },
];

const TONE = {
  green: { cls: "bg-[#A3E635]/12 border-[#A3E635]/30 text-[#A3E635]", dot: "bg-[#A3E635]" },
  red: { cls: "bg-red-500/12 border-red-500/30 text-red-300", dot: "bg-red-500" },
  amber: { cls: "bg-amber-500/12 border-amber-500/30 text-amber-300", dot: "bg-amber-400" },
  blue: { cls: "bg-sky-500/12 border-sky-500/30 text-sky-300", dot: "bg-sky-400" },
};

const CAT_ICON = {
  clients: Building2, leads: Inbox, tasks: CheckSquare, support: MessageSquare, invoices: FileText,
  reports: FileBarChart2, modules: Package, websites: Globe, assignments: Target, roles: SlidersHorizontal,
  users: UserCog, system: Database, upsell: TrendingUp, logins: LogIn, "2fa": ShieldCheck,
  password: KeyRound, locks: Lock,
};
const entryIcon = (e) => (e.source === "security" ? eventIcon(e.event) : CAT_ICON[e.category] || FileSearch);
const SEV_TONE = { red: "text-red-400", amber: "text-amber-400", lime: "text-[#A3E635]" };
const SEV_DOT = { red: "bg-red-500", amber: "bg-amber-400", lime: "bg-[#A3E635]" };

export function AuditTab({ ctx }) {
  const { t, nav } = ctx;
  const setTab = ctx.setTab;
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [fRange, setFRange] = useState("30d");
  const [fType, setFType] = useState("all");
  const [fUser, setFUser] = useState("all");
  const [fRole, setFRole] = useState("all");
  const [fStatus, setFStatus] = useState("all");
  const [fClient, setFClient] = useState("all");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [detail, setDetail] = useState(null);
  const wrap = (setter) => (v) => { setter(v); setPage(1); };

  const fetchData = useCallback(async () => {
    try { const r = await api.get("/admin/audit-trail"); setData(r.data); setError(false); }
    catch { setError(true); }
  }, []);
  useEffect(() => { fetchData(); }, [fetchData, ctx.lastRefreshed]);

  const entries = data?.entries || [];
  const userEmails = useMemo(
    () => Array.from(new Set(entries.map((e) => e.email).filter(Boolean))).sort(),
    [entries]
  );
  const companies = useMemo(
    () => Array.from(new Set(entries.map((e) => e.company_name).filter(Boolean))).sort(),
    [entries]
  );

  const rows = useMemo(() => {
    let l = entries;
    if (fRange !== "all") { const cut = Date.now() - RANGE_MS[fRange]; l = l.filter((e) => new Date(e.created_at).getTime() >= cut); }
    if (fType !== "all") l = l.filter((e) => e.category === fType);
    if (fStatus !== "all") l = l.filter((e) => e.bucket === fStatus);
    if (fRole === "super") l = l.filter((e) => e.role === "super_admin");
    if (fRole === "team") l = l.filter((e) => e.role === "team_member" || e.role === "super_admin");
    if (fRole === "client") l = l.filter((e) => e.role === "client" || e.role === "client_staff");
    if (fUser !== "all") l = l.filter((e) => e.email === fUser);
    if (fClient !== "all") l = l.filter((e) => e.company_name === fClient);
    const s = q.trim().toLowerCase();
    if (s) l = l.filter((e) =>
      (e.event_label || "").toLowerCase().includes(s) || (e.email || "").toLowerCase().includes(s) ||
      (e.name || "").toLowerCase().includes(s) || (e.ip || "").includes(s) ||
      (e.detail || "").toLowerCase().includes(s) || (e.company_name || "").toLowerCase().includes(s));
    return l;
  }, [entries, fRange, fType, fStatus, fRole, fUser, fClient, q]);

  const total = rows.length;
  const pages = Math.max(1, Math.ceil(total / perPage));
  const cur = Math.min(page, pages);
  const pageRows = rows.slice((cur - 1) * perPage, cur * perPage);
  const resetFilters = () => { setFType("all"); setFUser("all"); setFRole("all"); setFStatus("all"); setFClient("all"); setFRange("30d"); setQ(""); setPage(1); };

  const exportCsv = async () => {
    downloadCsv("audit-log.csv", [
      { label: "Zeitpunkt", value: (e) => deDT(e.created_at) },
      { label: "Ereignis", value: "event_label" }, { label: "Detail", value: "detail" },
      { label: "Benutzer", value: (e) => e.name || "" }, { label: "E-Mail", value: "email" },
      { label: "Rolle", value: (e) => e.role_label || "" }, { label: "Kunde", value: (e) => e.company_name || "" },
      { label: "IP", value: "ip" }, { label: "Gerät", value: (e) => e.device || "" },
      { label: "Ort", value: (e) => e.location || "" }, { label: "Status", value: "status_label" },
      { label: "Quelle", value: (e) => (e.source === "security" ? "Sicherheit" : "Aktivität") },
      { label: "Verifiziert", value: (e) => (e.verified ? "Ja" : "Nein") }, { label: "Seq", value: "seq" },
    ], rows);
    setExporting(true);
    try { await api.post("/admin/audit-log/export", { count: rows.length, format: "csv" }); toast.success(t("Export erstellt und protokolliert")); fetchData(); }
    catch { toast.error(t("Export konnte nicht protokolliert werden")); }
    finally { setExporting(false); }
  };

  const openEntry = (e) => {
    if (e.entity_type === "role") return setTab("roles");
    if (e.category === "assignments" || e.entity_type === "customer") return setTab("assign");
    if (e.entity_type === "user" || e.source === "security") {
      if (e.entity_id || e.email) return setTab(e.client_id ? "clients" : "team");
      return setDetail(e);
    }
    if (e.client_id) return nav(`/admin/clients/${e.client_id}`);
    return setDetail(e);
  };

  const copyDetail = async (e) => {
    const text = JSON.stringify(e, null, 2);
    try { if (navigator.clipboard?.writeText) { await navigator.clipboard.writeText(text); toast.success(t("Details kopiert")); return; } throw new Error("nc"); }
    catch { try { const ta = document.createElement("textarea"); ta.value = text; ta.style.position = "fixed"; ta.style.top = "-9999px"; document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta); toast.success(t("Details kopiert")); } catch { toast.error(t("Kopieren fehlgeschlagen")); } }
  };

  const integ = data?.integrity;
  const typeOptions = [{ value: "all", label: t("Alle Typen") }, ...(data?.categories || []).map((c) => ({ value: c.key, label: t(c.label) }))];

  return (
    <>
      {/* KPI-Karten (echte Backend-Daten, Letzte 30 Tage) */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="audit-kpis">
        {KPI_CONFIG.map(({ key, icon: Icon, accent }) => {
          const k = data?.kpis?.[key] || {};
          const a = A(accent);
          const up = (k.trend ?? 0) >= 0;
          return (
            <div key={key} data-testid={`audit-kpi-${key}`} className="bg-[#15151b] border border-white/[0.07] rounded-2xl p-4 hover:border-white/15 transition-colors">
              <div className="flex items-center justify-between">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${a.badge}`}><Icon className="w-4.5 h-4.5" /></div>
                {data ? <span className={`text-[11px] font-bold ${a.text}`}>{up ? "+" : ""}{k.trend}%</span> : null}
              </div>
              <div className={`text-2xl font-black mt-3 leading-none ${a.text}`}>{data ? nf(k.value) : "—"}</div>
              <div className="text-[11px] font-semibold text-gray-300 mt-1.5 truncate">{t(k.label || "")}</div>
              <div className="text-[10px] text-gray-500">{t(k.sub || "Letzte 30 Tage")}</div>
            </div>
          );
        })}
      </div>

      {/* Integritäts-Banner */}
      {integ && (
        <div data-testid="audit-integrity" className={`mt-4 flex items-center gap-2.5 rounded-xl border px-4 py-2.5 text-xs ${integ.verified ? "bg-[#A3E635]/[0.06] border-[#A3E635]/25 text-[#A3E635]" : "bg-red-500/[0.06] border-red-500/25 text-red-300"}`}>
          {integ.verified ? <ShieldCheck className="w-4 h-4 shrink-0" /> : <ShieldAlert className="w-4 h-4 shrink-0" />}
          <span className="font-semibold">
            {integ.verified
              ? `${t("Protokoll manipulationssicher")} · ${nf(integ.chained)} ${t("Einträge verkettet & geprüft")}`
              : `${t("Achtung: Integritätsprüfung fehlgeschlagen bei Eintrag")} #${integ.broken_at}`}
          </span>
          <span className="ml-auto text-gray-500 hidden md:inline">{t("Append-only · SHA-256 Hash-Kette")}</span>
        </div>
      )}

      {/* Filterleiste */}
      <Card className="p-4 mt-4" data-testid="sec-panel-audit">
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-7 gap-3 items-end">
          <Fld label={t("Zeitraum")}><Sel value={fRange} onChange={wrap(setFRange)} testid="audit-f-range" options={[{ value: "24h", label: t("Letzte 24 h") }, { value: "7d", label: t("Letzte 7 Tage") }, { value: "30d", label: t("Letzte 30 Tage") }, { value: "all", label: t("Alle") }]} /></Fld>
          <Fld label={t("Ereignistyp")}><Sel value={fType} onChange={wrap(setFType)} testid="audit-f-type" options={typeOptions} /></Fld>
          <Fld label={t("Benutzer")}><Sel value={fUser} onChange={wrap(setFUser)} testid="audit-f-user" options={[{ value: "all", label: t("Alle Benutzer") }, ...userEmails.map((em) => ({ value: em, label: em }))]} /></Fld>
          <Fld label={t("Rolle")}><Sel value={fRole} onChange={wrap(setFRole)} testid="audit-f-role" options={[{ value: "all", label: t("Alle Rollen") }, { value: "super", label: t("Super Admin") }, { value: "team", label: t("KEWAG-Team") }, { value: "client", label: t("Kundenbenutzer") }]} /></Fld>
          <Fld label={t("Status")}><Sel value={fStatus} onChange={wrap(setFStatus)} testid="audit-f-status" options={[{ value: "all", label: t("Alle Status") }, { value: "successful", label: t("Erfolgreich") }, { value: "warning", label: t("Warnung") }, { value: "critical", label: t("Kritisch") }]} /></Fld>
          <Fld label={t("Kunde")}><Sel value={fClient} onChange={wrap(setFClient)} testid="audit-f-client" options={[{ value: "all", label: t("Alle Kunden") }, ...companies.map((c) => ({ value: c, label: c }))]} /></Fld>
          <Fld label={t("Suche")}>
            <div className="relative"><Search className="w-3.5 h-3.5 text-gray-500 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input value={q} onChange={(e) => wrap(setQ)(e.target.value)} data-testid="audit-search" placeholder={t("Benutzer, IP, Ereignis, Details…")} className="w-full bg-black/40 border border-white/10 rounded-lg pl-8 pr-3 py-2 text-xs focus:border-[#A3E635] outline-none" /></div>
          </Fld>
        </div>
        <div className="flex items-center gap-3 mt-3 pt-3 border-t border-white/5">
          <span className="text-xs text-gray-500" data-testid="audit-count">{total === 0 ? 0 : (cur - 1) * perPage + 1}–{Math.min(total, cur * perPage)} {t("von")} {total} {t("Ereignissen")}</span>
          <button onClick={resetFilters} data-testid="audit-reset" className="text-xs text-gray-400 hover:text-white px-2 py-1">{t("Filter zurücksetzen")}</button>
          <div className="ml-auto"><ExportBtn onClick={exportCsv} label={exporting ? t("Exportiere…") : t("Exportieren")} disabled={total === 0 || exporting} title={t("Als CSV exportieren (wird protokolliert)")} testid="audit-export" /></div>
        </div>
      </Card>

      {/* Tabelle */}
      <Card className="p-0 overflow-hidden mt-4" data-testid="sec-audit-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-gray-500 border-b border-white/10">
                <th className="text-left font-semibold px-5 py-3">{t("Zeitpunkt")}</th>
                <th className="text-left font-semibold px-3 py-3">{t("Ereignistyp")}</th>
                <th className="text-left font-semibold px-3 py-3">{t("Benutzer")}</th>
                <th className="text-left font-semibold px-3 py-3 hidden lg:table-cell">{t("Rolle")}</th>
                <th className="text-left font-semibold px-3 py-3 hidden lg:table-cell">{t("Kunde / Firma")}</th>
                <th className="text-left font-semibold px-3 py-3 hidden 2xl:table-cell">{t("Details")}</th>
                <th className="text-left font-semibold px-3 py-3 hidden md:table-cell">{t("IP-Adresse")}</th>
                <th className="text-left font-semibold px-3 py-3 hidden xl:table-cell">{t("Gerät / Browser")}</th>
                <th className="text-left font-semibold px-3 py-3 hidden xl:table-cell">{t("Ort")}</th>
                <th className="text-left font-semibold px-3 py-3">{t("Status")}</th>
                <th className="text-right font-semibold px-5 py-3">{t("Aktionen")}</th>
              </tr>
            </thead>
            <tbody>
              {!data && !error && <tr><td colSpan={11} className="px-5 py-10 text-center"><Loader2 className="w-5 h-5 animate-spin text-[#A3E635] mx-auto" /></td></tr>}
              {error && <tr><td colSpan={11} className="px-5 py-8 text-center text-red-300 text-sm">{t("Audit-Log konnte nicht geladen werden.")}</td></tr>}
              {data && total === 0 && <tr><td colSpan={11} className="px-5 py-6 text-gray-500 text-sm">{t("Keine Ereignisse vorhanden.")}</td></tr>}
              {pageRows.map((e) => {
                const Icon = entryIcon(e);
                const tn = TONE[e.status_tone] || TONE.blue;
                return (
                  <tr key={`${e.source}-${e.id}-${e.seq}`} className="border-b border-white/5 last:border-0 hover:bg-white/[0.015] cursor-pointer" data-testid={`event-row-${e.id}`} onClick={() => setDetail(e)}>
                    <td className="px-5 py-2.5 text-gray-400 text-xs whitespace-nowrap">{deDT(e.created_at) || "—"}</td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2.5">
                        <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${tn.cls}`}><Icon className="w-3.5 h-3.5" /></div>
                        <div className="min-w-0">
                          <div className="font-medium whitespace-nowrap flex items-center gap-1.5">{e.event_label}{e.is_critical && <span className="w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" title={t("Kritische Aktion")} />}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2.5"><div className="text-xs font-medium truncate max-w-[160px]">{e.name || (e.email ? "—" : t("System"))}</div><div className="text-[11px] text-gray-500 truncate max-w-[160px]">{e.email || "—"}</div></td>
                    <td className="px-3 py-2.5 hidden lg:table-cell">{e.role_label ? <span className="text-[11px] text-gray-300">{e.role_label}</span> : <span className="text-gray-600 text-xs">—</span>}</td>
                    <td className="px-3 py-2.5 text-gray-400 text-xs truncate max-w-[140px] hidden lg:table-cell">{e.company_name || "—"}</td>
                    <td className="px-3 py-2.5 text-gray-500 text-[11px] truncate max-w-[220px] hidden 2xl:table-cell">{e.detail || "—"}</td>
                    <td className="px-3 py-2.5 text-gray-500 text-xs font-mono hidden md:table-cell">{e.ip || "—"}</td>
                    <td className="px-3 py-2.5 hidden xl:table-cell"><DeviceCell device={e.device} t={t} /></td>
                    <td className="px-3 py-2.5 hidden xl:table-cell text-xs">{e.location ? <span className="text-gray-300 inline-flex items-center gap-1.5"><span>{countryFlag(e.country)}</span>{e.location}</span> : <span className="text-gray-600">{t("Nicht verfügbar")}</span>}</td>
                    <td className="px-3 py-2.5"><span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${tn.cls}`}><span className={`w-1.5 h-1.5 rounded-full ${tn.dot}`} />{e.status_label}</span></td>
                    <td className="px-5 py-2.5">
                      <div className="flex items-center justify-end gap-1.5">
                        <button onClick={(ev) => { ev.stopPropagation(); setDetail(e); }} data-testid={`audit-detail-${e.id}`} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold text-gray-300 bg-white/5 hover:bg-white/10 border border-white/10 transition-colors" title={t("Details anzeigen")}><Eye className="w-3.5 h-3.5" /> {t("Details")}</button>
                        <button onClick={(ev) => { ev.stopPropagation(); openEntry(e); }} data-testid={`audit-open-${e.id}`} className="p-1.5 rounded-lg text-gray-400 hover:text-[#A3E635] hover:bg-white/10 border border-white/10 transition-colors" title={t("Öffnen")}><ExternalLink className="w-3.5 h-3.5" /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {total > 0 && <Pagination page={cur} setPage={setPage} total={total} perPage={perPage} setPerPage={setPerPage} t={t} testid="audit-pagination" />}
      </Card>

      {detail && (
        <Drawer title={t("Ereignis-Details")} onClose={() => setDetail(null)} testid="audit-drawer">
          <div className="mb-4 flex items-start gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${(TONE[detail.status_tone] || TONE.blue).cls}`}>{(() => { const I = entryIcon(detail); return <I className="w-5 h-5" />; })()}</div>
            <div className="min-w-0">
              <div className="font-bold text-sm flex items-center gap-2">{detail.event_label}<span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${(TONE[detail.status_tone] || TONE.blue).cls}`}>{detail.status_label}</span></div>
              {detail.detail && <div className="text-[11px] text-gray-500 mt-0.5">{detail.detail}</div>}
            </div>
          </div>

          <DrawerSection title={t("Übersicht")}>
            <KV label={t("Zeitpunkt")} value={deDT(detail.created_at) || "—"} />
            <KV label={t("Ereignis-ID")} value={detail.id} mono />
            <KV label={t("Status")} value={detail.status_label} />
            <KV label={t("Schweregrad")} value={<span className={`inline-flex items-center gap-1.5 ${SEV_TONE[detail.severity_tone] || ""}`}><span className={`w-1.5 h-1.5 rounded-full ${SEV_DOT[detail.severity_tone] || "bg-gray-500"}`} />{detail.severity_label}</span>} />
            <KV label={t("Quelle")} value={detail.source === "security" ? t("Sicherheitsprotokoll") : t("Aktivitätsprotokoll")} />
          </DrawerSection>

          <DrawerSection title={t("Benutzer")}>
            <KV label={t("Name")} value={detail.name || t("System")} />
            <KV label={t("E-Mail")} value={detail.email || "—"} />
            <KV label={t("Rolle")} value={detail.role_label || t("Nicht verfügbar")} />
          </DrawerSection>

          <DrawerSection title={t("Kontext")}>
            <KV label={t("Kunde / Firma")} value={detail.company_name || "—"} />
            <KV label={t("IP-Adresse")} value={detail.ip || t("Nicht verfügbar")} mono />
            <KV label={t("Ort")} value={detail.location ? <span className="inline-flex items-center gap-1.5"><span>{countryFlag(detail.country)}</span>{detail.location}</span> : t("Nicht verfügbar")} accent={detail.location ? "text-gray-200" : "text-gray-500"} />
            <KV label={t("Gerät")} value={detail.device || t("Nicht erfasst")} />
          </DrawerSection>

          {detail.changes && Object.keys(detail.changes).length > 0 && (
            <DrawerSection title={t("Änderungen")}>
              {Object.entries(detail.changes).map(([k, v]) => (
                <KV key={k} label={k} value={v && typeof v === "object" && "zu" in v ? `${v.von ?? "—"} → ${v.zu}` : String(v)} />
              ))}
            </DrawerSection>
          )}

          <DrawerSection title={t("Integrität & Kette")}>
            <KV label={t("Status")} value={detail.legacy ? <span className="text-gray-400">{t("Legacy (vor Verkettung)")}</span> : detail.verified ? <span className="inline-flex items-center gap-1.5 text-[#A3E635]"><ShieldCheck className="w-3.5 h-3.5" /> {t("Verifiziert")}</span> : <span className="text-amber-400">{t("Nicht verkettet")}</span>} />
            <KV label={t("Sequenz")} value={detail.seq != null ? `#${detail.seq}` : "—"} mono />
            <KV label={t("Integritäts-Hash")} value={detail.integrity_hash ? detail.integrity_hash.slice(0, 24) + "…" : "—"} mono />
            <KV label={t("Vorheriger Hash")} value={detail.previous_hash ? detail.previous_hash.slice(0, 24) + "…" : "—"} mono />
          </DrawerSection>

          <div className="mb-4">
            <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-gray-500 mb-2">{t("Roh-Daten (JSON)")}</div>
            <pre className="text-[10px] bg-black/50 border border-white/10 rounded-lg p-3 overflow-x-auto text-gray-300 max-h-48" data-testid="audit-drawer-json">{JSON.stringify(detail, null, 2)}</pre>
          </div>

          <div className="flex flex-col gap-2">
            <button onClick={() => openEntry(detail)} data-testid="audit-drawer-open" className="w-full bg-[#A3E635]/10 hover:bg-[#A3E635]/20 border border-[#A3E635]/30 text-[#A3E635] text-sm font-semibold py-2 rounded-lg flex items-center justify-center gap-2 transition-colors"><Link2 className="w-4 h-4" /> {t("Im Kontext öffnen")}</button>
            <button onClick={() => copyDetail(detail)} data-testid="audit-drawer-copy" className="w-full bg-white/5 hover:bg-white/10 border border-white/10 text-sm font-semibold py-2 rounded-lg flex items-center justify-center gap-2 transition-colors"><FileSearch className="w-4 h-4 text-[#A3E635]" /> {t("Details kopieren")}</button>
          </div>
        </Drawer>
      )}
    </>
  );
}

function Fld({ label, children }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">{label}</label>
      <div className="[&>select]:w-full [&>select]:py-2">{children}</div>
    </div>
  );
}
